import {
  type BoxDetail,
  type BoxPageCopy,
  type Currency,
  dropEntryNotices,
  dropTableDisclosureNotes,
  getItemChance,
  getItemRarity,
  resolveRarityColor,
  type StorefrontTerminology,
} from "@loot-os/api-contract";
import { type RarityTier, rarityTier } from "../../domain/rarity.js";
import type { BoxPageContentsContent, BoxPageDrop } from "./boxPageContent.js";

/** The tiers `getItemRarity` produces, in order. */
const PLATFORM_RARITIES = [0, 1, 2, 3, 4] as const;

/**
 * What the box can hold, with its published odds, from its drop table: every entry that can drop,
 * its chance, value and tier (value ÷ price), each limited or unshippable one with its badges, and
 * the table's notes, in the page's words and the platform's disclosure (`dropTableDisclosure.ts`).
 * A stand-in while the box loads. The storefront's box page and the theme preview both build the
 * contents with it, so the preview's odds are the ones a store's table would show.
 */
export function boxContentsContent(
  box: BoxDetail | null,
  copy: BoxPageCopy["contents"],
  currency: Currency,
  terms: StorefrontTerminology,
): BoxPageContentsContent {
  const frame = {
    title: copy.title,
    sortLabel: copy.sortLabel,
    sorts: { value: { label: copy.sorts.value }, odds: { label: copy.sorts.odds } },
  };
  if (!box) return { ...frame, state: { status: "loading" } };

  // An out-of-stock entry stays in the table at weight 0 so a roll verifies by index; it cannot
  // drop, so it is not listed.
  const entries = box.dropTable.filter((entry) => entry.weight > 0);
  // publishVersion does not enforce itemId uniqueness, so a duplicate is possible; suffix only
  // the repeats, keeping the common case's key stable.
  const seen = new Map<string, number>();
  const chanceByTier = new Map<RarityTier, number>();
  const drops = entries.map((entry): BoxPageDrop => {
    const nth = seen.get(entry.itemId) ?? 0;
    seen.set(entry.itemId, nth + 1);
    const rarity = getItemRarity(entry.value, box.priceCents);
    const tier = rarityTier(rarity);
    const chance = getItemChance(entry.weight);
    chanceByTier.set(tier, (chanceByTier.get(tier) ?? 0) + chance);
    return {
      id: nth === 0 ? entry.itemId : `${entry.itemId}#${nth}`,
      name: entry.name,
      image: entry.imageUrl,
      tier,
      tierLabel: resolveRarityColor(rarity).label,
      chance,
      value: entry.value,
      notices: dropEntryNotices(entry, currency, terms),
    };
  });
  const odds = PLATFORM_RARITIES.flatMap((rarity) => {
    const tier = rarityTier(rarity);
    const chance = chanceByTier.get(tier);
    return chance === undefined
      ? []
      : [{ tier, chance: Number(chance.toFixed(4)), label: resolveRarityColor(rarity).label }];
  });
  return {
    ...frame,
    count: copy.count(entries.length),
    state: {
      status: "ready",
      odds,
      drops,
      notes: dropTableDisclosureNotes(entries, terms),
    },
  };
}
